import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import {
  FaCopy, FaPen, FaPlus, FaListOl, FaRegClock, FaRegCalendarAlt, FaUsers,
  FaCheck, FaTimes, FaChevronDown,
} from 'react-icons/fa';
import { fetchTestById, updateTestsApi } from '../pages/services/apiService';
// Kept although this page no longer uses its classes: CSS here is global, and
// StudentDetails / QuestionDetails share class names (card-block, left-section,
// details-layout) that this file has always styled for them.
import "../styles/TestDetails.css";
import "../styles/TestDetailsPage.css";
import Loading from './ReusableComponent/Loading';
import PopupDialog from './ReusableComponent/PopupDialog';
import SliderComponent from './ReusableComponent/SliderComponent';
import { formatDateTime, useHighlightCode } from "./utils/utility";
import { difficultyColor } from "./utils/difficulty";
import { renderContentHtml } from "./utils/richText";

// ─── Display helpers ─────────────────────────────────────────────────────────

const STATUS_TONE = { Live: "live", Completed: "done", Upcoming: "upcoming", Draft: "draft" };

// "Weekly-test" -> "Weekly test"
const typeLabel = (t) => {
  const s = String(t || "").replace(/-/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Test";
};

const QUESTION_TYPE_SHORT = {
  "Multiple Choice single selection": "MCQ · Single",
  "Multiple Choice multi selection": "MCQ · Multi",
  "Code to compile with output": "Code",
  Descriptive: "Descriptive",
  Text: "Text",
};

// Question text is editor HTML; the list shows a plain, clamped preview.
const plainText = (html) => {
  const el = document.createElement("div");
  el.innerHTML = String(html || "");
  return (el.textContent || "").replace(/\s+/g, " ").trim();
};

const shortDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

const isTrue = (v) => v === true || String(v) === "true";

// ─── Small building blocks ───────────────────────────────────────────────────

const Card = ({ title, action, children, className = "" }) => (
  <section className={`tdp-card ${className}`}>
    {(title || action) && (
      <header className="tdp-card-head">
        {title && <h2>{title}</h2>}
        {action}
      </header>
    )}
    {children}
  </section>
);

const Stat = ({ icon, label, value, sub }) => (
  <div className="tdp-stat">
    <span className="tdp-stat-icon">{icon}</span>
    <div className="tdp-stat-text">
      <span className="tdp-stat-label">{label}</span>
      <span className="tdp-stat-value">{value}</span>
      {sub && <span className="tdp-stat-sub">{sub}</span>}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="tdp-field">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const Flag = ({ on, label }) => (
  <li className={`tdp-flag ${on ? "is-on" : "is-off"}`}>
    <span className="tdp-flag-icon">{on ? <FaCheck /> : <FaTimes />}</span>
    {label}
  </li>
);

// Instructions and descriptions can be long and carry images; they open
// folded so the page leads with what is scheduled and what is on the paper.
const RichBlock = ({ html, empty }) => {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setOverflowing(el.scrollHeight > 260);
    measure();
    // Images finish loading after the first measure and change the height.
    el.querySelectorAll("img").forEach((img) => img.addEventListener("load", measure, { once: true }));
    return undefined;
  }, [html]);

  if (!html || (plainText(html) === "" && !/<img/i.test(html))) {
    return <p className="tdp-muted">{empty}</p>;
  }
  return (
    <>
      <div
        ref={ref}
        className={`tdp-rich${overflowing && !expanded ? " is-folded" : ""}`}
        dangerouslySetInnerHTML={{ __html: renderContentHtml(html) }}
      />
      {overflowing && (
        <button type="button" className="tdp-link-btn" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "Show more"}
          <FaChevronDown className={expanded ? "is-flipped" : ""} />
        </button>
      )}
    </>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const TestDetails = () => {
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSlider, setShowSlider] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const pageRef = useRef(null);
  useHighlightCode(pageRef, [test]);

  useEffect(() => {
    const getTest = async () => {
      try {
        const data = await fetchTestById(id);
        setTest(data);
        setBatch(data?.batch || null);
      } catch (err) {
        setError('Error fetching test details');
      } finally {
        setLoading(false);
      }
    };
    getTest();
  }, [id, showSlider]);

  if (loading) return <Loading message="Loading Details..." />;
  if (error || !test) return <PopupDialog type="error" text="No test data found" />;

  const questions = Array.isArray(test.questions) ? test.questions : [];

  // Every batch the test is offered to. `batches` comes from the API; older
  // responses only carry the primary `batch`.
  const batches = Array.isArray(test.batches) && test.batches.length
    ? test.batches
    : (batch ? [batch] : []);
  const primaryBatchId = String(batch?._id || test.batchId || "");

  const subjects = (test.testSubject || []).map((s) => s.subject).filter(Boolean);
  const modules = (test.testModule || []).map((m) => m.module).filter(Boolean);
  const analysisOn = isTrue(test.isAnalysisRequired);

  // Columns that would be empty for every question are left out rather than
  // shown as a wall of blanks.
  const hasMarks = questions.some((q) => Number(q.markPositive) || Number(q.markNegative));
  const hasModule = questions.some((q) => q.module || q.subject);

  // Opens the Add Test form prefilled from this test: every detail — title,
  // batches, schedule, status, settings, description, instructions — except
  // its questions, which the new test gets on its own (AddTests.jsx).
  const handleCopy = () => navigate(`/tests/add?copyFrom=${test._id}`);
  const handleEdit = () => navigate(`/tests/${test._id}/update`);

  const handleAddUpdateQuestions = async (selectedIds) => {
    try {
      setIsUpdating(true);
      await updateTestsApi(test._id, { questionIds: Array.isArray(selectedIds) ? selectedIds : [] });
      setShowSlider(false);
      const updatedData = await fetchTestById(id);
      setTest(updatedData);
      setBatch(updatedData?.batch || null);
    } catch (err) {
      console.error("Add/Remove Question Error:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="tdp" ref={pageRef}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="tdp-head">
        <div className="tdp-head-main">
          <div className="tdp-eyebrow">
            {typeLabel(test.testType)}
            {subjects.length > 0 && <> · {subjects.join(", ")}</>}
          </div>
          <h1>{test.testTitle}</h1>
          <div className="tdp-head-meta">
            <span className={`tdp-pill tdp-pill-${STATUS_TONE[test.testStatus] || "neutral"}`}>
              {test.testStatus || "No status"}
            </span>
            <span>Created {formatDateTime(test.createdDateTime)}</span>
            <span className="tdp-dot" aria-hidden="true">•</span>
            <span>Updated {formatDateTime(test.updatedDateTime)}</span>
          </div>
        </div>
        <div className="tdp-actions">
          <button type="button" className="tdp-btn tdp-btn-ghost" onClick={handleCopy}>
            <FaCopy /> Copy to new test
          </button>
          <button type="button" className="tdp-btn tdp-btn-primary" onClick={handleEdit}>
            <FaPen /> Edit test
          </button>
        </div>
      </header>

      {/* ── At a glance ────────────────────────────────────────────────── */}
      <div className="tdp-stats">
        <Stat icon={<FaListOl />} label="Questions" value={questions.length} />
        <Stat icon={<FaRegClock />} label="Duration" value={test.testDuration || "—"}
              sub={isTrue(test.requiredTimer) ? "Timed" : "No timer"} />
        <Stat icon={<FaRegCalendarAlt />} label="Test window"
              value={shortDateTime(test.testStartFrom)}
              sub={`until ${shortDateTime(test.testStartTo)}`} />
        <Stat icon={<FaUsers />} label="Batches" value={batches.length || "—"}
              sub={batches.length === 1 ? batches[0].batchTitle : undefined} />
      </div>

      <div className="tdp-grid">
        {/* ── Main column ──────────────────────────────────────────────── */}
        <div className="tdp-main">
          <Card title="Schedule">
            <div className="tdp-schedule">
              <div>
                <h3 className="tdp-subhead">Test</h3>
                <dl className="tdp-fields">
                <Field label="Can start from">{formatDateTime(test.testStartFrom)}</Field>
                <Field label="Can start until">{formatDateTime(test.testStartTo)}</Field>
                <Field label="Ends at">{formatDateTime(test.testEndTime)}</Field>
                <Field label="Duration">{test.testDuration || "—"}</Field>
                </dl>
              </div>
              <div>
                <h3 className="tdp-subhead">Analysis</h3>
                {analysisOn ? (
                  <dl className="tdp-fields">
                    <Field label="Opens">{formatDateTime(test.analysisStartFrom)}</Field>
                    <Field label="Can open until">{formatDateTime(test.analysisStartTo)}</Field>
                    <Field label="Ends at">{formatDateTime(test.analysisEndTime)}</Field>
                    <Field label="Duration">{test.analysisDuration || "—"}</Field>
                  </dl>
                ) : (
                  <p className="tdp-muted">Students do not get an analysis for this test.</p>
                )}
              </div>
            </div>
          </Card>

          <Card title="Instructions">
            <RichBlock html={test.testInstructions} empty="No instructions added." />
          </Card>

          <Card title="Description">
            <RichBlock html={test.testDescription} empty="No description added." />
          </Card>
        </div>

        {/* ── Side column ──────────────────────────────────────────────── */}
        <aside className="tdp-side">
          <Card title="Settings">
            <ul className="tdp-flags">
              <Flag on={isTrue(test.requiredTimer)} label="Timer required" />
              <Flag on={isTrue(test.protectiveTest)} label="Protective test" />
              <Flag on={isTrue(test.isFullScreen)} label="Full screen" />
              <Flag on={analysisOn} label="Analysis for students" />
            </ul>
          </Card>

          <Card title="Coverage">
            <dl className="tdp-fields tdp-fields-stacked">
              <Field label="Subjects">
                {subjects.length ? subjects.map((s) => <span key={s} className="tdp-chip">{s}</span>) : "—"}
              </Field>
              <Field label="Modules">
                {modules.length ? modules.map((m) => <span key={m} className="tdp-chip">{m}</span>) : "—"}
              </Field>
              {test.testTopic?.topic && <Field label="Topic">{test.testTopic.topic}</Field>}
              {test.testSection?.section && <Field label="Section">{test.testSection.section}</Field>}
            </dl>
          </Card>

          <Card title={batches.length > 1 ? `Batches (${batches.length})` : "Batch"}>
            {batches.length ? (
              <ul className="tdp-batches">
                {batches.map((b) => (
                  <li key={b._id}>
                    <div className="tdp-batch-row">
                      <span className="tdp-batch-title">{b.batchTitle || "Untitled batch"}</span>
                      {b.batchStatus && (
                        <span className={`tdp-pill tdp-pill-${b.batchStatus === "Active" ? "live" : "neutral"}`}>
                          {b.batchStatus}
                        </span>
                      )}
                    </div>
                    {/* Timing is shown for the primary batch, the one the API
                        returns in full. */}
                    {String(b._id) === primaryBatchId && batch && (
                      <div className="tdp-batch-meta">
                        {batch.batchTime && <span>{batch.batchTime}</span>}
                        {Array.isArray(batch.batchDays) && batch.batchDays.length > 0 && (
                          <span>{batch.batchDays.map((d) => String(d).slice(0, 3)).join(" · ")}</span>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="tdp-muted">Not assigned to a batch.</p>
            )}
          </Card>
        </aside>
      </div>

      {/* ── Questions ──────────────────────────────────────────────────── */}
      <Card
        className="tdp-questions"
        title={`Questions (${questions.length})`}
        action={
          <button type="button" className="tdp-btn tdp-btn-ghost" onClick={() => setShowSlider(true)}>
            <FaPlus /> Add / remove questions
          </button>
        }
      >
        {isUpdating && <Loading message="Updating Questions..." />}
        {questions.length ? (
          <div className="tdp-table-wrap">
            <table className="tdp-table">
              <thead>
                <tr>
                  <th className="tdp-col-num">#</th>
                  <th>Question</th>
                  <th>Type</th>
                  <th>Difficulty</th>
                  {hasModule && <th>Module</th>}
                  {hasMarks && <th className="tdp-col-marks">Marks</th>}
                </tr>
              </thead>
              <tbody>
                {questions.map((q, i) => {
                  const text = plainText(q.question);
                  return (
                    <tr key={q._id}>
                      <td className="tdp-col-num">{i + 1}</td>
                      <td>
                        <Link className="tdp-q-text" to={`/questions/${q._id}`} title={text}>
                          {text || <em>Untitled question</em>}
                        </Link>
                      </td>
                      <td className="tdp-nowrap">{QUESTION_TYPE_SHORT[q.questionType] || q.questionType || "—"}</td>
                      <td className="tdp-nowrap">
                        {q.difficultyLevel ? (
                          <span className="tdp-diff" style={{ "--diff": difficultyColor(q.difficultyLevel) }}>
                            {q.difficultyLevel}
                          </span>
                        ) : "—"}
                      </td>
                      {hasModule && <td className="tdp-muted-cell">{q.module || q.subject || "—"}</td>}
                      {hasMarks && (
                        <td className="tdp-col-marks tdp-nowrap">
                          <span className="tdp-mark-pos">+{Number(q.markPositive) || 0}</span>
                          {" / "}
                          <span className="tdp-mark-neg">−{Number(q.markNegative) || 0}</span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="tdp-empty">
            <p>No questions on this paper yet.</p>
            <button type="button" className="tdp-btn tdp-btn-primary" onClick={() => setShowSlider(true)}>
              <FaPlus /> Add questions
            </button>
          </div>
        )}
      </Card>

      <SliderComponent
        open={showSlider}
        onClose={() => setShowSlider(false)}
        handleAddUpdateQuestions={handleAddUpdateQuestions}
        selectedQuestions={questions.map((q) => q._id)}
        batchId={batch?._id || test.batchId}
      />
    </div>
  );
};

export default TestDetails;
